import { SelectedAsset } from "@/store/assetsExplorer";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import {
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
} from "@/util/safeLocalStorage";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AssetItem } from "./shared/AssetItem";

interface LatestUsedItem {
  type: "image" | "video";
  path: string;
  timestamp: number;
}

type FileType = "image" | "video" | "all";

interface LatestUsedTabProps {
  threadId?: string;
  fileType?: FileType;
  onSelect: (asset: SelectedAsset) => void;
  stopSignal?: number;
}

// Helper function to get localStorage key
const getLatestUsedStorageKey = (threadId?: string) => {
  return threadId ? `vidmuse:thread:${threadId}:latest_used_assets` : null;
};

// Helper function to load latest used items from localStorage
export const loadLatestUsedItems = (threadId?: string): LatestUsedItem[] => {
  const storageKey = getLatestUsedStorageKey(threadId);
  if (!storageKey) return [];

  try {
    const stored = safeLocalStorageGetItem(storageKey);
    if (stored) {
      const items = JSON.parse(stored) as LatestUsedItem[];
      // Sort by timestamp descending (most recent first)
      return items.sort((a, b) => b.timestamp - a.timestamp);
    }
  } catch (error) {
    console.error("Failed to load latest used items:", error);
  }

  return [];
};

// Helper function to check if there are any latest used items
export const hasLatestUsedItems = (
  threadId?: string,
  fileType: FileType = "all",
): boolean => {
  const items = loadLatestUsedItems(threadId);
  if (fileType === "all") {
    return items.length > 0;
  }
  return items.some((item) => item.type === fileType);
};

// Helper function to save an item to latest used
export const saveLatestUsedItem = (
  threadId: string | undefined,
  type: "image" | "video",
  path: string,
) => {
  const storageKey = getLatestUsedStorageKey(threadId);
  if (!storageKey) return;

  try {
    const items = loadLatestUsedItems(threadId);

    // Remove existing item with same path if it exists
    const filteredItems = items.filter((item) => item.path !== path);

    // Add new item at the beginning
    const newItem: LatestUsedItem = {
      type,
      path,
      timestamp: Date.now(),
    };

    // Keep only the last 50 items
    const updatedItems = [newItem, ...filteredItems].slice(0, 50);

    safeLocalStorageSetItem(storageKey, JSON.stringify(updatedItems));
  } catch (error) {
    console.error("Failed to save latest used item:", error);
  }
};

export function LatestUsedTab(props: LatestUsedTabProps) {
  const { threadId, fileType = "all", onSelect, stopSignal } = props;
  const [items, setItems] = useState<LatestUsedItem[]>([]);

  // Load items on mount and when threadId changes
  useEffect(() => {
    const loadedItems = loadLatestUsedItems(threadId);
    setItems(loadedItems);
  }, [threadId]);

  // Listen to storage events to update when other tabs modify the list
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      const storageKey = getLatestUsedStorageKey(threadId);
      if (e.key === storageKey) {
        const loadedItems = loadLatestUsedItems(threadId);
        setItems(loadedItems);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [threadId]);

  // Filter items based on fileType
  const filteredItems = useMemo(() => {
    if (fileType === "all") {
      return items;
    }
    return items.filter((item) => item.type === fileType);
  }, [items, fileType]);

  const handleSelect = useCallback(
    (item: LatestUsedItem) => {
      onSelect({ type: item.type, path: item.path });
    },
    [onSelect],
  );

  if (filteredItems.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-text-tertiary">
        No recently used assets
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {filteredItems.map((item, index) => {
        const src = convertFilePathToUrl(item.path, { level: "medium" });
        return (
          <AssetItem
            key={`latest_used_${item.path}-${index}`}
            src={src}
            previewSrc={src}
            alt={item.type === "image" ? "Image" : "Video"}
            mediaType={item.type}
            onClick={() => handleSelect(item)}
            stopSignal={stopSignal}
          />
        );
      })}
    </div>
  );
}
